import { useState, useEffect, useRef } from 'react';
import { Box } from '@mui/material';

let handleOpenDialog;
let setImageLoaded;

export const setHandlers = (openDialogHandler, imageLoadedHandler) => {
  handleOpenDialog = openDialogHandler;
  setImageLoaded = imageLoadedHandler;
};

export const ImageWithBoundingBoxes = ({ imageData, clickable = true, hoveredObjectType = null }) => {
  const { image_url, metadata } = imageData;
  const boundingBoxes = metadata[0]?.tray_det_bboxes || [];
  const [hoveredBox, setHoveredBox] = useState(null);
  //eslint-disable-next-line no-unused-vars
  const [hoveredProductBox, setHoveredProductBox] = useState(null);
  const [hoveredAllianPop, setHoveredAllianPop] = useState(null);
  const [clickedProductBox, setClickedProductBox] = useState(null);

  const handleProductBoxClick = (index) => {
    if (clickedProductBox === index) {
      setClickedProductBox(null);
    } else {
      setClickedProductBox(index);
    }
  };

  const [imageSize, setImageSize] = useState({ width: 0, height: 0 }); // natural size
  const [renderedSize, setRenderedSize] = useState({ width: 0, height: 0 }); // on-screen size
  const imageRef = useRef(null);
  const containerRef = useRef(null);

  const handleImageLoad = () => {
    if (imageRef.current) {
      const { naturalWidth, naturalHeight } = imageRef.current;
      setImageSize({ width: naturalWidth, height: naturalHeight });
      setImageLoaded(true);
      // After image loads, measure rendered size
      setTimeout(() => {
        if (imageRef.current) {
          const rect = imageRef.current.getBoundingClientRect();
          setRenderedSize({ width: rect.width, height: rect.height });
        }
      }, 50);
    }
  };

  // Re-measure on window resize
  useEffect(() => {
    const handleResize = () => {
      if (imageRef.current) {
        const rect = imageRef.current.getBoundingClientRect();
        setRenderedSize({ width: rect.width, height: rect.height });
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Calculate letterboxing offset
  let offsetX = 0,
    offsetY = 0,
    scale = 1;
  if (imageSize.width && imageSize.height && renderedSize.width && renderedSize.height) {
    const containerWidth = renderedSize.width;
    const containerHeight = renderedSize.height;
    const imageAspect = imageSize.width / imageSize.height;
    const containerAspect = containerWidth / containerHeight;
    if (imageAspect > containerAspect) {
      // Image is wider than container
      scale = containerWidth / imageSize.width;
      offsetY = (containerHeight - imageSize.height * scale) / 2;
    } else {
      // Image is taller than container
      scale = containerHeight / imageSize.height;
      offsetX = (containerWidth - imageSize.width * scale) / 2;
    }
  }

  return (
    <Box
      ref={containerRef}
      sx={{
        position: 'relative',
        width: '100%',
        height: '100%',
        maxWidth: clickable ? '300px' : '100%',
        margin: '0 auto',
        // overflow: 'hidden',
        overflow: 'visible',
        cursor: clickable ? 'pointer' : 'default',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        background: '#fff'
      }}
      onClick={clickable ? () => handleOpenDialog(imageData) : undefined}
    >
      <img
        ref={imageRef}
        src={image_url}
        alt="Bay Image"
        style={{
          width: '100%',
          height: '100%',
          objectFit: 'contain',
          display: 'block',
          borderRadius: '8px'
        }}
        onLoad={handleImageLoad}
      />

      {/* Render tray_det_bboxes */}
      {imageSize.width > 0 &&
        renderedSize.width > 0 &&
        boundingBoxes.map((box, idx) => {
          const { xmin, ymin, xmax, ymax, name, type } = box;
          const boxType = name || type || 'Unknown';
          const isTypeHighlighted = hoveredObjectType && boxType === hoveredObjectType;
          console.log('box', box.isMatched);
          // Calculate scaled positions
          const left = offsetX + xmin * scale;
          const top = offsetY + ymin * scale;
          const width = (xmax - xmin) * scale;
          const height = (ymax - ymin) * scale;

          return (
            <Box
              key={`tray-${idx}`}
              sx={{
                position: 'absolute',
                left: left,
                top: top,
                width: width,
                height: height,
                border: `3px solid ${
                  box.isMatched === false || (box.isMatched === true && box?.miss_location)
                    ? '#FF0000' // red for non-matched items
                    : isTypeHighlighted && boxType === 'empty'
                    ? '#e57373' // red for empty type highlight
                    : isTypeHighlighted
                    ? '#81C784' // green for others
                    : '#44BE54'
                }`,
                // border: "2px solid blue",
                boxSizing: 'border-box',
                borderRadius: '4px',
                pointerEvents: 'auto',
                backgroundColor:
                  box.isMatched === false
                    ? 'rgba(255, 0, 0, 0.2)'
                    : isTypeHighlighted && boxType === 'empty'
                    ? 'rgba(229, 115, 115, 0.3)'
                    : isTypeHighlighted
                    ? 'rgba(129, 199, 132, 0.3)'
                    : 'transparent',
                transition: 'all 0.3s ease',
                transform: 'translateZ(0)',
                cursor: 'pointer',
                zIndex: 5
              }}
              onMouseEnter={() => setHoveredBox(idx)}
              onMouseLeave={() => setHoveredBox(null)}
            />
          );
        })}

      {/* Render product_bboxes_list with a lighter green color */}
      {imageSize.width > 0 &&
        renderedSize.width > 0 &&
        metadata[0]?.tray_det_bboxes
          ?.flatMap((tray) => tray.product_bboxes_list || [])
          .map((box, idx) => {
            const { xmin, ymin, xmax, ymax } = box;

            // Find matching product in productMatchingResult if it exists
            let groupLabel = '';
            let productInfo = null;

            if (metadata[0]?.productMatchingResult) {
              metadata[0].productMatchingResult.forEach((match) => {
                if (match.productIndices) {
                  const matchingProduct = match.productIndices.find(
                    (prod) => prod.xmin === xmin && prod.ymin === ymin && prod.xmax === xmax && prod.ymax === ymax
                  );

                  if (matchingProduct) {
                    groupLabel = `G-${match.vmIndex + 1}`;
                    productInfo = {
                      detectedDescription: match.detectedDescription || 'N/A',
                      vmDescription: match.vmDescription || 'N/A',
                      isMatched: match.isMatched,
                      facingAnomaly: match.facingAnomaly || false,
                      productCount: match.productCount || 0,
                      expectedFacings: match.expectedFacings || 0
                    };
                  }
                }
              });
            }

            const left = offsetX + xmin * scale;
            const top = offsetY + ymin * scale;
            const width = (xmax - xmin) * scale;
            const height = (ymax - ymin) * scale;

            return (
              <>
                <Box
                  key={`product-${idx}`}
                  sx={{
                    position: 'absolute',
                    left,
                    top,
                    width,
                    height,
                    border: `3px solid ${
                      productInfo && (productInfo.isMatched === false || productInfo.facingAnomaly === true) ? '#FF0000' : '#8AE28A'
                    }`,
                    // border: "2px solid blue",
                    boxSizing: 'border-box',
                    borderRadius: '4px',
                    pointerEvents: 'auto',
                    backgroundColor:
                      productInfo && (productInfo.isMatched === false || productInfo.facingAnomaly === true)
                        ? 'rgba(255, 0, 0, 0.2)'
                        : 'transparent',
                    transition: 'all 0.3s ease',
                    transform: 'translateZ(0)',
                    cursor: 'pointer',
                    zIndex: 5
                  }}
                  // onClick={() => handleProductBoxClick(idx)}
                  onMouseEnter={() => handleProductBoxClick(idx)}
                  onMouseLeave={() => handleProductBoxClick(null)}
                />

                {groupLabel && (
                  <Box
                    sx={{
                      position: 'absolute',
                      left: left,
                      top: top - 20,
                      backgroundColor: 'rgba(0, 0, 0, 0.7)',
                      color: 'white',
                      padding: '2px 6px',
                      fontSize: '12px',
                      fontWeight: 'bold',
                      borderRadius: '4px',
                      zIndex: 6,
                      pointerEvents: 'none'
                    }}
                  >
                    {groupLabel}
                  </Box>
                )}

                {clickedProductBox === idx && productInfo && (
                  <Box
                    sx={{
                      position: 'absolute',
                      left: left + width / 2,
                      top: top - 20,
                      transform: 'translate(-50%, -100%)',
                      // optional gap between box and tooltip
                      backgroundColor: 'rgba(20, 20, 20, 0.95)',
                      color: 'white',
                      padding: '12px 16px',
                      borderRadius: '8px',
                      fontSize: '12px',
                      whiteSpace: 'normal',
                      zIndex: 109999,
                      pointerEvents: 'none',
                      minWidth: '220px',
                      maxWidth: '280px',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
                      border: '1px solid rgba(255,255,255,0.1)'
                    }}
                  >
                    <div style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}>
                      <div
                        style={{
                          fontSize: '13px',
                          fontWeight: 'bold',
                          marginBottom: '10px',
                          borderBottom: '1px solid rgba(255,255,255,0.2)',
                          paddingBottom: '6px'
                        }}
                      >
                        Product Information
                      </div>

                      {(productInfo.isMatched === false || productInfo.facingAnomaly === true) && (
                        <div
                          style={{
                            marginBottom: '12px',
                            backgroundColor: 'rgba(255,82,82,0.15)',
                            padding: '8px 10px',
                            borderRadius: '6px',
                            border: '1px solid rgba(255,82,82,0.3)'
                          }}
                        >
                          <div
                            style={{
                              fontSize: '11px',
                              marginBottom: '4px',
                              color: '#ff5252',
                              fontWeight: 'bold',
                              textTransform: 'uppercase',
                              letterSpacing: '0.5px'
                            }}
                          >
                            Anomaly Detected
                          </div>
                          <div
                            style={{
                              color: '#ff5252',
                              fontWeight: '600',
                              fontSize: '13px'
                            }}
                          >
                            {productInfo.isMatched === false ? 'Incorrect Product' : 'Incorrect Facing'}
                          </div>
                        </div>
                      )}

                      {productInfo.facingAnomaly === true && (
                        <div
                          style={{
                            marginBottom: '12px',
                            backgroundColor: 'rgba(255,255,255,0.05)',
                            padding: '8px 10px',
                            borderRadius: '6px'
                          }}
                        >
                          <div
                            style={{
                              fontSize: '11px',
                              marginBottom: '6px',
                              color: '#bbb',
                              fontWeight: 'bold',
                              textTransform: 'uppercase',
                              letterSpacing: '0.5px'
                            }}
                          >
                            Facing Details
                          </div>
                          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px', alignItems: 'center' }}>
                            <span style={{ fontSize: '12px', color: '#ff9e80' }}>Current:</span>
                            <span style={{ fontSize: '14px', color: '#ff9e80', fontWeight: 'bold' }}>{productInfo.productCount}</span>
                          </div>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <span style={{ fontSize: '12px', color: '#8bc34a' }}>Expected:</span>
                            <span style={{ fontSize: '14px', color: '#8bc34a', fontWeight: 'bold' }}>{productInfo.expectedFacings}</span>
                          </div>
                        </div>
                      )}

                      <div
                        style={{
                          backgroundColor: 'rgba(255,255,255,0.05)',
                          padding: '8px 10px',
                          borderRadius: '6px'
                        }}
                      >
                        <div
                          style={{
                            fontSize: '11px',
                            marginBottom: '6px',
                            color: '#bbb',
                            fontWeight: 'bold',
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px'
                          }}
                        >
                          Product Details
                        </div>

                        <div style={{ marginBottom: '10px' }}>
                          <div style={{ fontSize: '11px', marginBottom: '4px', color: productInfo.isMatched ? '#8bc34a' : '#ff9e80' }}>
                            Detected Product:
                          </div>
                          <div
                            style={{
                              color: productInfo.isMatched ? 'white' : '#ff9e80',
                              fontWeight: productInfo.isMatched ? 'normal' : '500',
                              fontSize: '13px',
                              lineHeight: '1.3'
                            }}
                          >
                            {productInfo.detectedDescription}
                          </div>
                        </div>

                        <div>
                          <div style={{ fontSize: '11px', marginBottom: '4px', color: '#8bc34a' }}>VM Product:</div>
                          <div
                            style={{
                              color: '#8bc34a',
                              fontSize: '13px',
                              fontWeight: '500',
                              lineHeight: '1.3'
                            }}
                          >
                            {productInfo.vmDescription}
                          </div>
                        </div>
                      </div>
                    </div>
                  </Box>
                )}
              </>
            );
          })}

      {imageSize.width > 0 &&
        renderedSize.width > 0 &&
        metadata[0]?.allian_pop?.map((pop, idx) => {
          const { xmin, ymin, xmax, ymax } = pop.popItem;

          const left = offsetX + xmin * scale;
          const top = offsetY + ymin * scale;
          const width = (xmax - xmin) * scale;
          const height = (ymax - ymin) * scale;

          return (
            <Box
              key={`allian-pop-${idx}`}
              sx={{
                position: 'absolute',
                left,
                top,
                width,
                height,
                border: `3px ${pop?.miss_location ? 'solid' : 'dashed'} ${pop?.miss_location ? '#FF0000' : '#e53935'}`, // Make solid red if miss_location exists
                boxSizing: 'border-box',
                borderRadius: '4px',
                pointerEvents: 'auto',
                backgroundColor: pop?.miss_location ? 'rgba(255, 0, 0, 0.2)' : 'rgba(229, 57, 53, 0.1)', // Brighter red background if miss_location exists
                transition: 'all 0.3s ease',
                transform: 'translateZ(0)',
                cursor: 'pointer',
                zIndex: 6
              }}
              onMouseEnter={() => setHoveredAllianPop(idx)}
              onMouseLeave={() => setHoveredAllianPop(null)}
            />
          );
        })}

      {/* Global tooltip for tray box */}
      {hoveredBox != null && boundingBoxes[hoveredBox]?.ocr_result && (
        <Box
          sx={{
            position: 'absolute',
            left: '20px',
            top: '20px',
            transform: 'none',
            backgroundColor: 'rgba(20, 20, 20, 0.95)',
            color: 'white',
            padding: '12px 16px',
            borderRadius: '8px',
            fontSize: '12px',
            whiteSpace: 'normal',
            zIndex: 9999,
            pointerEvents: 'none',
            minWidth: '220px',
            maxWidth: '280px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
            border: '1px solid rgba(255,255,255,0.1)'
          }}
        >
          {(() => {
            const { ocr_result, isMatched, vmDescription, miss_location } = boundingBoxes[hoveredBox];
            return (
              <div style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}>
                <div
                  style={{
                    fontSize: '13px',
                    fontWeight: 'bold',
                    marginBottom: '10px',
                    borderBottom: '1px solid rgba(255,255,255,0.2)',
                    paddingBottom: '6px'
                  }}
                >
                  SEL Information
                </div>

                {isMatched && miss_location && (
                  <div
                    style={{
                      marginBottom: '12px',
                      backgroundColor: 'rgba(255,82,82,0.15)',
                      padding: '8px 10px',
                      borderRadius: '6px',
                      border: '1px solid rgba(255,82,82,0.3)'
                    }}
                  >
                    <div
                      style={{
                        fontSize: '11px',
                        marginBottom: '4px',
                        color: '#ff5252',
                        fontWeight: 'bold',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px'
                      }}
                    >
                      Correct Location
                    </div>
                    <div
                      style={{
                        color: '#ff5252',
                        fontWeight: '600',
                        fontSize: '13px'
                      }}
                    >
                      {miss_location}
                    </div>
                  </div>
                )}
                {isMatched === false && (
                  <div
                    style={{
                      marginBottom: '12px',
                      backgroundColor: 'rgba(255,82,82,0.15)',
                      padding: '8px 10px',
                      borderRadius: '6px',
                      border: '1px solid rgba(255,82,82,0.3)'
                    }}
                  >
                    <div
                      style={{
                        fontSize: '11px',
                        marginBottom: '4px',
                        color: '#ff5252',
                        fontWeight: 'bold',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px'
                      }}
                    >
                      Incorrect POP
                    </div>
                    {/* <div style={{ 
                        color: '#ff5252', 
                        fontWeight: '600',
                        fontSize: '13px'
                      }}>
                        {miss_location}
                      </div> */}
                  </div>
                )}

                {ocr_result && (
                  <div
                    style={{
                      backgroundColor: 'rgba(255,255,255,0.05)',
                      padding: '8px 10px',
                      borderRadius: '6px'
                    }}
                  >
                    <div
                      style={{
                        fontSize: '11px',
                        marginBottom: '6px',
                        color: '#bbb',
                        fontWeight: 'bold',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px'
                      }}
                    >
                      Detected Details
                    </div>

                    {ocr_result.product_description && (
                      <div style={{ marginBottom: '6px' }}>
                        <div style={{ fontSize: '11px', marginBottom: '2px', color: isMatched ? '#8bc34a' : '#ff9e80' }}>Product:</div>
                        <div style={{ color: isMatched ? 'white' : '#ff9e80', fontSize: '13px' }}>{ocr_result.product_description}</div>
                      </div>
                    )}
                    {ocr_result.price && (
                      <div>
                        <div style={{ fontSize: '11px', marginBottom: '2px', color: isMatched ? '#8bc34a' : '#ff9e80' }}>Price:</div>
                        <div style={{ color: isMatched ? 'white' : '#ff9e80', fontSize: '13px', fontWeight: 'bold' }}>
                          ₹{ocr_result.price}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {vmDescription && (
                  <div
                    style={{
                      marginTop: '10px',
                      backgroundColor: 'rgba(255,255,255,0.05)',
                      padding: '8px 10px',
                      borderRadius: '6px'
                    }}
                  >
                    <div
                      style={{
                        fontSize: '11px',
                        marginBottom: '6px',
                        color: '#8bc34a',
                        fontWeight: 'bold',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px'
                      }}
                    >
                      VM Promo
                    </div>
                    <div style={{ color: '#8bc34a', fontSize: '13px' }}>{vmDescription}</div>
                  </div>
                )}
              </div>
            );
          })()}
        </Box>
      )}

      {/* Tooltip for allian_pop items */}
      {hoveredAllianPop != null && metadata[0]?.allian_pop[hoveredAllianPop] && (
        <Box
          sx={{
            position: 'absolute',
            left: '20px',
            top: '20px',
            transform: 'none',
            backgroundColor: 'rgba(20, 20, 20, 0.95)',
            color: 'white',
            padding: '12px 16px',
            borderRadius: '8px',
            fontSize: '12px',
            whiteSpace: 'normal',
            zIndex: 9999,
            pointerEvents: 'none',
            minWidth: '220px',
            maxWidth: '280px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
            border: '1px solid rgba(255,255,255,0.1)'
          }}
        >
          <div style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}>
            {/* Title */}
            <div
              style={{
                fontSize: '13px',
                fontWeight: 'bold',
                marginBottom: '10px',
                borderBottom: '1px solid rgba(255,255,255,0.2)',
                paddingBottom: '6px'
              }}
            >
              POP Item Details
            </div>

            {/* When isMatched is false, show Anomaly Type: Incorrect */}
            {metadata[0].tray_det_bboxes[hoveredAllianPop]?.isMatched === false && (
              <div
                style={{
                  marginBottom: '12px',
                  backgroundColor: 'rgba(255,82,82,0.15)',
                  padding: '8px 10px',
                  borderRadius: '6px',
                  border: '1px solid rgba(255,82,82,0.3)'
                }}
              >
                <div
                  style={{
                    fontSize: '11px',
                    marginBottom: '4px',
                    color: '#ff5252',
                    fontWeight: 'bold',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }}
                >
                  Anomaly Type
                </div>
                <div
                  style={{
                    color: '#ff5252',
                    fontWeight: '600',
                    fontSize: '13px'
                  }}
                >
                  Incorrect
                </div>
              </div>
            )}

            {metadata[0].tray_det_bboxes[hoveredAllianPop]?.isMatched === true && (
              <div
                style={{
                  marginBottom: '12px',
                  backgroundColor: 'rgba(255,82,82,0.15)',
                  padding: '8px 10px',
                  borderRadius: '6px',
                  border: '1px solid rgba(255,82,82,0.3)'
                }}
              >
                <div
                  style={{
                    fontSize: '11px',
                    marginBottom: '4px',
                    color: '#ff5252',
                    fontWeight: 'bold',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }}
                >
                  Correct Location
                </div>
                <div
                  style={{
                    color: '#ff5252',
                    fontWeight: '600',
                    fontSize: '13px'
                  }}
                >
                  {metadata[0].tray_det_bboxes[hoveredAllianPop]?.miss_location}
                </div>
              </div>
            )}

            <div
              style={{
                backgroundColor: 'rgba(255,255,255,0.05)',
                padding: '8px 10px',
                borderRadius: '6px'
              }}
            >
              <div
                style={{
                  fontSize: '11px',
                  marginBottom: '6px',
                  color: '#bbb',
                  fontWeight: 'bold',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px'
                }}
              >
                Item Properties
              </div>

              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
                <span style={{ fontSize: '12px', color: '#bbb' }}>Type:</span>
                <span style={{ fontSize: '12px', color: 'white', fontWeight: '500' }}>POP</span>
              </div>

              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ fontSize: '12px', color: '#bbb' }}>Status:</span>
                <span
                  style={{
                    fontSize: '12px',
                    color:
                      metadata[0].tray_det_bboxes[hoveredAllianPop]?.isMatched === false ||
                      metadata[0].tray_det_bboxes[hoveredAllianPop]?.miss_location
                        ? '#ff5252'
                        : '#8bc34a',
                    fontWeight: '500'
                  }}
                >
                  {metadata[0]?.tray_det_bboxes[hoveredAllianPop]?.isMatched === false
                    ? 'Incorrect'
                    : metadata[0]?.tray_det_bboxes[hoveredAllianPop]?.miss_location
                    ? 'Incorrect Location'
                    : 'Valid'}
                </span>
              </div>
            </div>
          </div>
        </Box>
      )}

      {hoveredProductBox != null &&
        metadata[0]?.tray_det_bboxes?.flatMap((tray) => tray.product_bboxes_list || [])[hoveredProductBox]?.ocr_result && (
          <Box
            sx={{
              position: 'absolute',
              left:
                offsetX +
                metadata[0].tray_det_bboxes.flatMap((tray) => tray.product_bboxes_list || [])[hoveredProductBox].xmin * scale +
                ((metadata[0].tray_det_bboxes.flatMap((tray) => tray.product_bboxes_list || [])[hoveredProductBox].xmax -
                  metadata[0].tray_det_bboxes.flatMap((tray) => tray.product_bboxes_list || [])[hoveredProductBox].xmin) *
                  scale) /
                  2,
              top:
                offsetY + metadata[0].tray_det_bboxes.flatMap((tray) => tray.product_bboxes_list || [])[hoveredProductBox].ymin * scale - 8,
              transform: 'translate(-50%, -100%)',
              backgroundColor: 'rgba(0, 0, 0, 0.85)',
              color: 'white',
              padding: '8px 12px',
              borderRadius: '4px',
              fontSize: '12px',
              whiteSpace: 'nowrap',
              zIndex: 9999,
              pointerEvents: 'none',
              minWidth: 'max-content'
            }}
          >
            {(() => {
              const productData = metadata[0].tray_det_bboxes.flatMap((tray) => tray.product_bboxes_list || [])[hoveredProductBox];
              const { ocr_result, isMatched, vmDescription } = productData;

              return (
                <div>
                  {ocr_result && (
                    <div>
                      {ocr_result.product_description && (
                        <div>
                          <span style={{ color: isMatched ? 'white' : '#ff9e80' }}>{ocr_result.product_description}</span>
                        </div>
                      )}
                      {ocr_result.price && (
                        <div>
                          <span style={{ color: isMatched ? 'white' : '#ff9e80' }}>₹{ocr_result.price}</span>
                        </div>
                      )}
                      {ocr_result.promotional_details && (
                        <div>
                          <span style={{ color: isMatched ? 'white' : '#ff9e80' }}>{ocr_result.promotional_details}</span>
                        </div>
                      )}
                    </div>
                  )}

                  {!isMatched && vmDescription && (
                    <div style={{ marginTop: '4px', borderTop: '1px solid rgba(255, 255, 255, 0.3)', paddingTop: '4px' }}>
                      <div style={{ fontSize: '10px', marginBottom: '2px', color: '#8bc34a' }}>VM Description:</div>
                      <div>
                        <span style={{ color: '#8bc34a' }}>{vmDescription}</span>
                      </div>
                    </div>
                  )}
                </div>
              );
            })()}
          </Box>
        )}
    </Box>
  );
};
